import { Link, useRouterState } from "@tanstack/react-router";
import { Car, Menu, Phone, UserRound } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "@/components/site/Logo";
import { FlagGB } from "@/components/site/FlagIcon";
import { enPathForNl } from "@/lib/i18n-urls";
import { trackBookCtaClick, trackPhoneClick } from "@/lib/tracking";
import { useCustomerLocale } from "@/hooks/use-customer-locale";
import { toast } from "sonner";

/** Taalwissel: gaat altijd naar de exacte Engelse tegenhanger. */
function LanguageSwitch() {
  const { setLocale } = useCustomerLocale();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const en = enPathForNl(pathname) ?? "/en";
  return (
    <a
      href={en}
      onClick={() => { void setLocale("en").catch(() => toast.error("Language preference could not be saved.")); }}
      hrefLang="en"
      aria-label="Switch to English"
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary hover:text-foreground"
    >
      <FlagGB />
      <span>EN<span className="sr-only">glish</span></span>
    </a>
  );
}


/** Compacte hoofdnavigatie: alleen de kernpagina's, de rest staat op die pagina's zelf. */
const MAIN_NAV = [
  { to: "/boeken", label: "Taxi boeken" },
  { to: "/prijzen", label: "Prijzen" },
  { to: "/luchthaventaxi", label: "Luchthavenvervoer" },
  { to: "/hoe-het-werkt", label: "Hoe werkt het?" },
  { to: "/contact", label: "Contact" },
] as const;

export function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border/80 bg-background/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center">
          <Logo priority />
        </Link>

        <nav aria-label="Hoofdnavigatie" className="hidden items-center gap-1 lg:flex">
          {MAIN_NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              activeProps={{ className: "text-foreground bg-secondary" }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitch />
          <Link

            to="/klant/login"
            className="hidden items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground md:inline-flex"
          >
            <UserRound className="size-4" /> Mijn ritten
          </Link>
          <Button asChild size="sm" className="font-semibold">
            <Link to="/boeken" onClick={() => trackBookCtaClick("header")}>
              Boek nu
            </Link>
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Menu">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 overflow-y-auto">
              <div className="mt-8 flex flex-col gap-1">
                {MAIN_NAV.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-base font-medium hover:bg-secondary"
                  >
                    {item.label}
                  </Link>
                ))}
                <Link
                  to="/klant/login"
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-base font-medium hover:bg-secondary"
                >
                  Mijn ritten
                </Link>
                <Link
                  to="/chauffeur"
                  onClick={() => setOpen(false)}
                  className="mt-4 rounded-lg border-t border-border px-3 pb-2.5 pt-4 text-base font-medium text-muted-foreground hover:bg-secondary"
                >
                  Voor chauffeurs
                </Link>
                <Link
                  to="/chauffeur/login"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-base font-medium text-muted-foreground hover:bg-secondary"
                >
                  <Car className="size-4" /> Chauffeurs inloggen
                </Link>

                <a
                  href="tel:+31850607086"
                  onClick={() => trackPhoneClick("header")}
                  className="mt-4 flex items-center gap-2 border-t border-border px-3 pt-4 text-base font-medium text-muted-foreground"
                >
                  <Phone className="size-4" /> 085 060 7086
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
